import {
    faFolder as solidFaFolder,
    faFolderOpen as solidFaFolderOpen,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionModal, Label } from '@kcftech/design-system';
import {
    ContentType,
    IPictureFileMetadataResponse,
    MoveFilesRequest,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useInfiniteFileLibraryDataSource } from 'custom_hooks/data_hooks/data_source/fileLibraryDataSource';
import { useDrainDataSource } from 'custom_hooks/data_hooks/data_source/useDrainDataSource';
import { useFolderLoader } from 'pages/Settings/FileLibrary/Folder/useFolderLoader';
import { useEffect, useMemo, useState } from 'react';
import { DataTree } from 'shared/data_tree/DataTree';
import { createTree } from 'shared/data_tree/tree';
import {
    TreeExpansionConfig,
    TreeNodeRenderer,
    TreeSelectionConfig,
} from 'shared/data_tree/types';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { toggleArrayItem } from 'utils/array';
import { queryClient } from 'utils/query';

export type MoveFolderModalProps = Readonly<{
    /**
     * Selected file/folder to be moved
     */
    fileToBeMoved: Pick<
        IPictureFileMetadataResponse,
        'DisplayName' | 'Id' | 'ParentFolderId'
    >;
    /**
     * Callback for when move was successful
     */
    onSave: () => void;
    /**
     * Callback for when move was cancelled
     */
    onCancel: () => void;
    locationId?: string;
    corporationId?: string;
    foldersHierarchy: IPictureFileMetadataResponse[];
}>;

const FolderItem = styled.span<{ $selected: boolean }>`
    background: ${({ $selected }) => ($selected ? tokens.text.link : 'none')};
    color: ${({ $selected }) => ($selected ? tokens.on.primary : 'none')};
    padding: 0.25rem;
`;

const FileToBeMovedItem = styled.span`
    color: ${tokens.text.link};
`;

const renderNode: TreeNodeRenderer<IPictureFileMetadataResponse> = (info) =>
    info.isSelectable ? (
        <FolderItem $selected={info.isSelected}>
            <FontAwesomeIcon
                icon={info.isExpanded ? solidFaFolderOpen : solidFaFolder}
                color={tokens.warning.default}
                className="mr-5"
            />
            {info.node.data.DisplayName}
        </FolderItem>
    ) : (
        <FileToBeMovedItem>
            <FontAwesomeIcon icon={solidFaFolderOpen} className="mr-5" />
            {info.node.data.DisplayName}
        </FileToBeMovedItem>
    );

export function DirentMoveModal({
    fileToBeMoved,
    onSave,
    onCancel,
    locationId,
    corporationId,
    foldersHierarchy,
}: MoveFolderModalProps) {
    const { showToast } = useNotificationContext();
    const [
        destinationFolder,
        setDestinationFolder,
    ] = useState<IPictureFileMetadataResponse>();
    const [tree, setTree] = useState(() => createTree<IPictureFileMetadataResponse>());
    const [expandedFolderIds, setExpandedFolderIds] = useState<string[]>([]);
    const { loadChildFolders, loadingIds } = useFolderLoader();
    const parentIds = useMemo(
        () => foldersHierarchy.map((folder) => folder.ParentFolderId ?? ''),
        [foldersHierarchy]
    );

    const { results: rootFolders, isLoading: isLoadingRootFolders } = useDrainDataSource(
        useInfiniteFileLibraryDataSource({
            name: 'file-library-table',
            defaultFilter: {
                nameSearch: '',
                corporationId,
                locationId,
                fileTypes: [ContentType.Folder],
                isRootFolder: true,
            },
        })
    );

    const { mutate: moveFolder, isPending: isMovingFolder } = useMutation({
        mutationFn: async () => {
            if (!destinationFolder) {
                throw new Error('Missing destination folder');
            }
            await sdClient.moveFiles({
                body: new MoveFilesRequest({
                    FileIds: [fileToBeMoved.Id],
                    DestinationFolderId: destinationFolder.Id,
                }),
            });
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['file-library-table'],
            });
            onSave();
            showToast({
                type: 'success',
                content: `${fileToBeMoved.DisplayName} successfully moved to ${destinationFolder?.DisplayName}.`,
            });
        },

        onError: () => {
            showToast({
                type: 'error',
                content: `An error occurred while moving ${fileToBeMoved.DisplayName}.`,
            });
        },
    });

    const expandable = useMemo<TreeExpansionConfig<IPictureFileMetadataResponse>>(
        () => ({
            isExpanded: (node) => expandedFolderIds.includes(node.id),
            onChange: (event) => {
                setExpandedFolderIds((current) =>
                    toggleArrayItem(current, event.node.id)
                );
            },
            canExpand: ({ data: node }) =>
                !!node.FolderCount && node.Id !== fileToBeMoved.Id,
            isLoading: ({ data: folder }) => loadingIds.includes(folder.Id),
            onLoadChildren: ({ data: folder }) =>
                loadChildFolders({
                    parentId: folder.Id,
                    onSuccess: (childFolders) =>
                        setTree((current) =>
                            current.replaceChildren({
                                id: folder.Id,
                                childData: childFolders.map((childFolder) => ({
                                    id: childFolder.Id,
                                    data: childFolder,
                                })),
                            })
                        ),
                }),
        }),
        [expandedFolderIds, loadChildFolders, loadingIds, fileToBeMoved.Id]
    );

    const selectable = useMemo<TreeSelectionConfig<IPictureFileMetadataResponse>>(
        () => ({
            selectionStyle: 'custom',
            isSelected: (node) => destinationFolder === node.data,
            onChange: ({ node }) => setDestinationFolder(node.data),
            canSelect: (node) => node.id !== fileToBeMoved.Id,
        }),
        [destinationFolder, fileToBeMoved.Id]
    );

    useEffect(() => {
        if (rootFolders) {
            setTree(
                createTree<IPictureFileMetadataResponse>(
                    rootFolders.map((folder) => ({ id: folder.Id, data: folder }))
                )
            );
        }
    }, [rootFolders]);

    useEffect(() => {
        if (!fileToBeMoved.ParentFolderId) {
            throw new Error('Missing parent folder');
        }
        setExpandedFolderIds([fileToBeMoved.ParentFolderId, ...parentIds]);
    }, [fileToBeMoved.ParentFolderId, foldersHierarchy, parentIds]);

    const title = useMemo(
        () => (
            <div>
                {'Move folder '}
                <span className="bold">{fileToBeMoved.DisplayName}</span>
                {' to '}
                <span className="bold">{destinationFolder?.DisplayName ?? ''}</span>
            </div>
        ),
        [destinationFolder?.DisplayName, fileToBeMoved.DisplayName]
    );

    return (
        <ActionModal
            data-testid="move-folder-modal"
            height={500}
            width={750}
            title={title}
            actionButtonText="Move"
            actionable={isMovingFolder || destinationFolder !== undefined}
            closeButtonText="Cancel"
            onClose={onCancel}
            onAction={moveFolder}
        >
            <Label label="Select a Folder">
                <SpinnerArea isLoading={isLoadingRootFolders}>
                    <DataTree
                        treeId="folder-tree"
                        data={tree}
                        renderNode={renderNode}
                        expandable={expandable}
                        selectable={selectable}
                    />
                </SpinnerArea>
            </Label>
        </ActionModal>
    );
}
